/**
 * @typedef {{toDate: () => Date}} FirestoreTimestamp
 */

/**
 * @param {number} n
 * @returns {string} Twee cijfers, met een voorloopnul waar nodig
 */
const pad2 = (n) => String(n).padStart(2, '0')

/**
 * @param {Date} date - Dag waarvan de korte weergave gemaakt wordt
 * @param {Date} time - Afspraak waaruit de tijd komt
 * @returns {string} Bijvoorbeeld 'di 01-09, 09:00'
 */
const shortDateTime = (date, time) => {
    const samengesteld = new Date(date)
    samengesteld.setHours(time.getHours(), time.getMinutes(), 0, 0)

    return samengesteld.toLocaleString('nl-NL', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/**
 * De regel onder de kop van het afspraakvenster: wanneer, hoe laat, en of hij
 * zich herhaalt.
 *
 * **Waarom de dag apart meekomt.** De agenda klapt een herhaalreeks uit in losse
 * dagen, maar het Firestore-document draagt de datum van de **eerste** keer.
 * Wie in september op een afspraak klikte kreeg daardoor 'di 07-01' te zien en
 * dacht dat hij de verkeerde afspraak open had. De tijden kloppen wél — die
 * horen bij elke herhaling — dus alleen de dag komt van buiten.
 * AI-CONTEXT: See docs/pitfalls.md#state-recurring-event-wrong-date
 *
 * Zonder aangeklikte dag valt hij terug op de datum van het document: de
 * takenlijst van een huishouden opent dezelfde editor buiten de agenda om, en
 * daar is dat de juiste datum.
 *
 * @param {object} params
 * @param {{Timestamp?: FirestoreTimestamp, TimestampEnd?: FirestoreTimestamp}|null} params.task - De afspraak
 * @param {Date} [params.occurrenceDate] - De dag die in de agenda is aangeklikt
 * @param {string} [params.repeatText] - Herhaling in gewone taal, al vertaald
 * @returns {string} Bijvoorbeeld 'di 01-09, 09:00 • 09:00–17:00 • elke week op dinsdag'
 */
const buildPlanningSummary = ({ task, occurrenceDate, repeatText = '' }) => {
    if (!task) return ''

    let start = null
    let end = null

    try {
        start = task.Timestamp ? task.Timestamp.toDate() : null
        end = task.TimestampEnd ? task.TimestampEnd.toDate() : null
    } catch (e) {
        start = null
        end = null
    }

    if (!start) return [repeatText].filter(Boolean).join(' • ') || 'Geen starttijd'

    const startLabel = shortDateTime(occurrenceDate || start, start)
    const timeRange = end
        ? `${pad2(start.getHours())}:${pad2(start.getMinutes())}–${pad2(end.getHours())}:${pad2(end.getMinutes())}`
        : 'Hele dag'

    return [startLabel, timeRange, repeatText].filter(Boolean).join(' • ')
}

export default buildPlanningSummary
